const analyticsEventValues = {
  signUp: 'sign_up',
  login: 'login',
  logout: 'logout',
  passwordResetRequest: 'password_reset_request',
  passwordResetComplete: 'password_reset_complete',
  emailVerificationResend: 'email_verification_resend',
  emailVerificationComplete: 'email_verification_complete',
  profileRegistrationStart: 'profile_registration_start',
  profileRegistrationStep: 'profile_registration_step',
  profileRegistrationComplete: 'profile_registration_complete',
  invoiceListView: 'invoice_list_view',
  invoiceDetailView: 'invoice_detail_view',
  invoiceCreateStart: 'invoice_create_start',
  invoiceCreateComplete: 'invoice_create_complete',
  invoiceFileUpload: 'invoice_file_upload',
  invoiceResubmit: 'invoice_resubmit',
  invoiceDownload: 'invoice_download',
  workReportListView: 'work_report_list_view',
  workReportDetailView: 'work_report_detail_view',
  workReportCreateStart: 'work_report_create_start',
  workReportContractSelect: 'work_report_contract_select',
  workReportDraftSave: 'work_report_draft_save',
  workReportSubmit: 'work_report_submit',
  workReportEditStart: 'work_report_edit_start',
  workReportEditComplete: 'work_report_edit_complete',
  contractListView: 'contract_list_view',
  contractDetailView: 'contract_detail_view',
  contractDownload: 'contract_download',
  messageListView: 'message_list_view',
  messageDetailView: 'message_detail_view',
  scoutListView: 'scout_list_view',
  scoutDetailView: 'scout_detail_view',
  companyDetailView: 'company_detail_view',
} as const;

export const ANALYTICS_EVENTS = {
  ...analyticsEventValues,
  SIGN_UP: analyticsEventValues.signUp,
  LOGIN: analyticsEventValues.login,
  LOGOUT: analyticsEventValues.logout,
  PASSWORD_RESET_REQUEST: analyticsEventValues.passwordResetRequest,
  PASSWORD_RESET_COMPLETE: analyticsEventValues.passwordResetComplete,
  EMAIL_VERIFICATION_RESEND: analyticsEventValues.emailVerificationResend,
  EMAIL_VERIFICATION_COMPLETE: analyticsEventValues.emailVerificationComplete,
  PROFILE_REGISTRATION_START: analyticsEventValues.profileRegistrationStart,
  PROFILE_REGISTRATION_STEP: analyticsEventValues.profileRegistrationStep,
  PROFILE_REGISTRATION_COMPLETE: analyticsEventValues.profileRegistrationComplete,
  INVOICE_LIST_VIEW: analyticsEventValues.invoiceListView,
  INVOICE_DETAIL_VIEW: analyticsEventValues.invoiceDetailView,
  INVOICE_CREATE_START: analyticsEventValues.invoiceCreateStart,
  INVOICE_CREATE_COMPLETE: analyticsEventValues.invoiceCreateComplete,
  INVOICE_FILE_UPLOAD: analyticsEventValues.invoiceFileUpload,
  INVOICE_RESUBMIT: analyticsEventValues.invoiceResubmit,
  INVOICE_DOWNLOAD: analyticsEventValues.invoiceDownload,
  WORK_REPORT_LIST_VIEW: analyticsEventValues.workReportListView,
  WORK_REPORT_DETAIL_VIEW: analyticsEventValues.workReportDetailView,
  WORK_REPORT_CREATE_START: analyticsEventValues.workReportCreateStart,
  WORK_REPORT_CONTRACT_SELECT: analyticsEventValues.workReportContractSelect,
  WORK_REPORT_DRAFT_SAVE: analyticsEventValues.workReportDraftSave,
  WORK_REPORT_SUBMIT: analyticsEventValues.workReportSubmit,
  WORK_REPORT_EDIT_START: analyticsEventValues.workReportEditStart,
  WORK_REPORT_EDIT_COMPLETE: analyticsEventValues.workReportEditComplete,
  CONTRACT_LIST_VIEW: analyticsEventValues.contractListView,
  CONTRACT_DETAIL_VIEW: analyticsEventValues.contractDetailView,
  CONTRACT_DOWNLOAD: analyticsEventValues.contractDownload,
  MESSAGE_LIST_VIEW: analyticsEventValues.messageListView,
  MESSAGE_DETAIL_VIEW: analyticsEventValues.messageDetailView,
  SCOUT_LIST_VIEW: analyticsEventValues.scoutListView,
  SCOUT_DETAIL_VIEW: analyticsEventValues.scoutDetailView,
  COMPANY_DETAIL_VIEW: analyticsEventValues.companyDetailView,
} as const;

export const PROFILE_REGISTRATION_STEPS = {
  10: { step: 10, step_name: 'hourly_rate' },
  20: { step: 20, step_name: 'project_preferences' },
  30: { step: 30, step_name: 'current_situation' },
  40: { step: 40, step_name: 'resume_upload' },
  60: { step: 60, step_name: 'job_experience' },
  70: { step: 70, step_name: 'language_experience' },
  80: { step: 80, step_name: 'skills_experience' },
  90: { step: 90, step_name: 'account_creation' },
  100: { step: 100, step_name: 'complete' },
} as const;

const TRACKED_EVENT_QUERY_KEY = '__tracked_event';
const TRACKED_EVENT_PAYLOAD_QUERY_KEY = '__tracked_event_params';
const LEGACY_TRACKED_EVENT_QUERY_KEY = '__track_event';
const LEGACY_TRACKED_EVENT_PAYLOAD_QUERY_KEY = '__track_payload';

const TRACKED_EVENT_QUERY_KEYS = [
  TRACKED_EVENT_QUERY_KEY,
  LEGACY_TRACKED_EVENT_QUERY_KEY,
] as const;
const TRACKED_EVENT_PAYLOAD_QUERY_KEYS = [
  TRACKED_EVENT_PAYLOAD_QUERY_KEY,
  LEGACY_TRACKED_EVENT_PAYLOAD_QUERY_KEY,
] as const;

export type AnalyticsPrimitive = string | number | boolean | null | undefined;
export type AnalyticsPayload = Record<string, AnalyticsPrimitive>;
export type AnalyticsEventParams = AnalyticsPayload;
export type AnalyticsEventName =
  (typeof analyticsEventValues)[keyof typeof analyticsEventValues];
export type ProfileRegistrationStep = keyof typeof PROFILE_REGISTRATION_STEPS;

type ParsedTrackedEvent = {
  event: AnalyticsEventName;
  payload: AnalyticsPayload;
  params: AnalyticsEventParams;
};

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

const ANALYTICS_EVENT_NAMES = new Set<AnalyticsEventName>(
  Object.values(analyticsEventValues),
);

const isAnalyticsEventName = (value: string): value is AnalyticsEventName => {
  return ANALYTICS_EVENT_NAMES.has(value as AnalyticsEventName);
};

const isAnalyticsPrimitive = (value: unknown): value is AnalyticsPrimitive => {
  return (
    value === null ||
    value === undefined ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  );
};

const sanitizeAnalyticsPayload = (value: unknown): AnalyticsPayload => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(value).filter(([, entryValue]) => isAnalyticsPrimitive(entryValue)),
  );
};

export const trackEvent = (
  event: AnalyticsEventName,
  payload: AnalyticsPayload = {},
) => {
  if (typeof window === 'undefined') {
    return;
  }

  const sanitizedPayload = Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined),
  );

  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push({
    event,
    ...sanitizedPayload,
  });
};

export const buildTrackedPath = (
  path: string,
  event: AnalyticsEventName,
  payload: AnalyticsPayload = {},
) => {
  const isAbsoluteUrl = /^https?:\/\//.test(path);
  // Relative path handling needs a valid base URL for URL constructor.
  const url = isAbsoluteUrl
    ? new URL(path)
    : new URL(path, 'http://analytics.local');

  const hashParams = new URLSearchParams(url.hash.replace(/^#/, ''));
  const sanitizedPayload = sanitizeAnalyticsPayload(payload);

  hashParams.set(TRACKED_EVENT_QUERY_KEY, event);

  if (Object.keys(sanitizedPayload).length > 0) {
    hashParams.set(
      TRACKED_EVENT_PAYLOAD_QUERY_KEY,
      JSON.stringify(sanitizedPayload),
    );
  } else {
    hashParams.delete(TRACKED_EVENT_PAYLOAD_QUERY_KEY);
  }

  url.hash = hashParams.toString();

  if (isAbsoluteUrl) {
    return url.toString();
  }

  return `${url.pathname}${url.search}${url.hash}`;
};

export const parseTrackedEvent = (
  searchParams: URLSearchParams,
): ParsedTrackedEvent | null => {
  const trackedEvent = TRACKED_EVENT_QUERY_KEYS
    .map((key) => searchParams.get(key))
    .find((value): value is string => Boolean(value));

  if (!trackedEvent || !isAnalyticsEventName(trackedEvent)) {
    return null;
  }

  const serializedPayload = TRACKED_EVENT_PAYLOAD_QUERY_KEYS
    .map((key) => searchParams.get(key))
    .find((value): value is string => Boolean(value));

  if (!serializedPayload) {
    return {
      event: trackedEvent,
      payload: {},
      params: {},
    };
  }

  try {
    const payload = sanitizeAnalyticsPayload(JSON.parse(serializedPayload));
    return {
      event: trackedEvent,
      payload,
      params: payload,
    };
  } catch (error) {
    console.error('Failed to parse tracked analytics payload:', error);
    return {
      event: trackedEvent,
      payload: {},
      params: {},
    };
  }
};

export const removeTrackedEventParams = (searchParams: URLSearchParams) => {
  const nextSearchParams = new URLSearchParams(searchParams);

  for (const key of TRACKED_EVENT_QUERY_KEYS) {
    nextSearchParams.delete(key);
  }

  for (const key of TRACKED_EVENT_PAYLOAD_QUERY_KEYS) {
    nextSearchParams.delete(key);
  }

  return nextSearchParams;
};
